<template>
	<div class="formWrapper">
		<el-form style="padding-bottom: 20px" @keyup.native.enter="isExistingCredential ? update() : create()"
			ref="form" :rules="formRules" label-position="top" label-width="100px" :model="form">
			<el-form-item prop="name" label="Credential Name">
				<el-input :disabled="isReadOnly" maxlength="50" placeholder="My credential's name"
					v-model="form.name"></el-input>
			</el-form-item>
			<el-form-item v-if="isOAuth2Cred" label="OAuth Callback URL">
				<el-input @click.native="copy(oAuth2CallbackUrl)" class="callbackUrl" disabled
					v-model="oAuth2CallbackUrl"></el-input>
				<p style="font-size: 10px; word-break: break-word; margin: 0px; line-height: 20px; margin-top: 5px">
					Specify this callback URL when prompted by the OAuth provider. <b>Click to copy</b>
				</p>
			</el-form-item>
			<span :key="property.name + i" v-for="(property, i) in credentialInterfaceProperties">
				<el-form-item v-if="property.type !== 'hidden' && displayRulesMet(property.displayOptions)"
					:prop="'data.' + property.name" :label="property.displayName">
					<el-input :disabled="isReadOnly" autocomplete="new-password"
						:show-password="property.typeOptions && property.typeOptions.password ? true : false"
						v-if="property.type === 'string'"
						:placeholder="isReadOnly ? 'Credential hidden for security' : (property.placeholder || '')"
						v-model="form.data[property.name]"></el-input>
					<el-switch :disabled="isReadOnly" v-if="property.type === 'boolean'"
						v-model="form.data[property.name]"> </el-switch>
					<el-input-number :disabled="isReadOnly"
						:placeholder="isReadOnly ? 'Credential hidden for security' : (property.placeholder || '')"
						:min="0" v-if="property.type === 'number'" v-model="form.data[property.name]"
						:step="1"></el-input-number>
					<el-select :disabled="isReadOnly" v-if="property.type === 'options'" no-data-text="No options found"
						no-match-text="No options found" filterable style="width: 100%"
						v-model="form.data[property.name]" placeholder="Select">
						<el-option v-for="(option, i) in property.options" :key="option.value + i" :label="option.name"
							:value="option.value">
						</el-option>
					</el-select>
					<!-- KEY FIX: only render fixedCollection when form.data[property.name] is ready -->
					<fixedCollectionInterfaceElement
						v-if="property.type === 'fixedCollection' && form.data[property.name]"
						:form="form.data[property.name]" :rootForm="form.data" :fixedCollectionObject="property"
						:integrationInterface="credentialInterface" :hideVariableToggle="true" />
					<p style="font-size: 10px; word-break: break-word; margin: 0px; line-height: 20px; margin-top: 5px"
						v-if="property.description" v-html="property.description"></p>
					<p style="font-size: 10px; word-break: break-word; margin: 0px; line-height: 20px; margin-top: 5px"
						v-if="property.hint" v-html="'Hint: ' + property.hint"></p>
				</el-form-item>
			</span>
			<el-form-item v-if="!isReadOnly">
				<el-button @click="isExistingCredential ? update() : create()" style="width: 100%; margin-top: 12px"
					type="primary" :loading="operationInProgress">{{ isExistingCredential ? "Update Credential" :
						"Create Credential" }}
				</el-button>
			</el-form-item>
		</el-form>
	</div>
</template>

<script>
import fixedCollectionInterfaceElement from "./fixedCollectionInterfaceElement.vue";
export default {
	components: { fixedCollectionInterfaceElement },
	props: {
		credentialInterface: Object,
		isExistingCredential: Boolean,
		isOwner: Boolean,
	},
	computed: {
		isReadOnly() {
			return !this.isOwner;
		},
	},
	watch: {
		credentialInterface: {
			handler(val) {
				if (!val) return;
				if (this.isExistingCredential) {
					if (!val.type) return; // minimum required field
					this.determineIfIsOAuth2(val.type);
					// Don't gate on val.properties — it comes separately
					if (val.data) {
						this.populateFormDataFromExistingCredential();
					}
				} else {
					if (!val.name) return;
					this.determineIfIsOAuth2(val.name);
					if (val.properties) {
						this.populateFormData();
					}
				}
			},
			deep: true,
			immediate: true,
		},
	},
	data() {
		return {
			oAuth2CallbackUrl: `${process.env.VUE_APP_RUNTIME_ENDPOINT}/oauth2-credential/callback`,
			isOAuth2Cred: false,
			operationInProgress: false,
			form: { type: "", name: "", data: {} },
			formRules: {
				name: [{ required: true, message: "A credential name is required", trigger: "change" }],
				data: {},
			},
			credentialInterfaceProperties: [],
		};
	},
	methods: {
		determineIfIsOAuth2(type) {
			this.isOAuth2Cred = type.toLowerCase().includes("oauth2") ? true : false;
		},
		evaluateConditionsInData(context) {
			if (typeof context === "object") {
				const newObj = {};
				for (const key in context) {
					newObj[key] = this.replaceCodeInString(context[key], context);
				}
				return newObj;
			} else {
				return context;
			}
		},
		replaceCodeInString(str, context) {
			if (typeof str !== "string" || !str.startsWith("=")) {
				return str;
			}
			str = str.substring(1);
			const pattern = /{{(.*?)}}/g;
			return str.replace(pattern, (_, code) => {
				code = `let $self = ${JSON.stringify(context)};
        const result = ${code}; 
        result`;
				try {
					return eval(code);
				} catch (error) {
					return `ERROR: ${error.message}`;
				}
			});
		},
		update() {
			this.$refs.form.validate(async (valid) => {
				if (valid) {
					this.form.data = this.evaluateConditionsInData(this.form.data);
					this.operationInProgress = true;
					const result = await this.httpHelper("RUNTIME", "PUT", "credentials/updateOne", {
						credentialId: this.credentialInterface.id,
						...this.form,
					});
					setTimeout(async () => {
						if (!result?.error) {
							this.$emit("credentialUpdated", {
								credentialId: this.credentialInterface.id,
								...this.form,
							});
							if (this.isOAuth2Cred) {
								await this.oAuth2Callback();
							} else {
								this.operationInProgress = false;
								this.$notify({ title: "Success", message: "Credential updated", type: "success", position: "bottom-left" });
							}
						} else {
							this.operationInProgress = false;
							this.$notifyError(this, result.error);
						}
					}, 500);
				}
			});
		},
		create() {
			this.$refs.form.validate(async (valid) => {
				if (valid) {
					this.form.data = this.evaluateConditionsInData(this.form.data);
					this.operationInProgress = true;
					const result = await this.httpHelper("RUNTIME", "POST", "credentials/createOne", this.form);
					setTimeout(async () => {
						if (!result?.error) {
							this.$emit("credentialCreated", result.response.credential);
							this.form = { type: "", name: "", data: {} };
							this.$set(this.form, "data", {});
							this.$refs.form.resetFields();
							if (this.isOAuth2Cred) {
								await this.oAuth2Callback(result.response.credential.id);
							} else {
								this.operationInProgress = false;
								this.$notify({ title: "Success", message: "Credential created", type: "success", position: "bottom-left" });
							}
						} else {
							this.operationInProgress = false;
							this.$notifyError(this, result.error);
						}
					}, 500);
				}
			});
		},
		async oAuth2Callback(id) {
			const result = await this.httpHelper("RUNTIME", "GET", "oauth2-credential/auth", {
				credentialId: id || this.credentialInterface.id,
			});
			if (!result?.error) {
				let messageRecieved = false;
				window.onmessage = function () { messageRecieved = true; };
				const win = window.open(result.response);
				const timer = setInterval(() => {
					if (win.closed) {
						this.operationInProgress = false;
						clearInterval(timer);
						if (messageRecieved) {
							this.$notify({ title: "OAuth2", message: "Authentication Flow Completed", type: "success", position: "bottom-left" });
						} else {
							this.$notify({ title: "OAuth2", message: "Unable to complete authentication flow. Possible issue with credentials", type: "error", position: "bottom-left", duration: 0 });
						}
						setTimeout(() => {
							this.$notify({ title: "Success", message: id ? "Credential created" : "Credential updated", type: "success", position: "bottom-left" });
						}, 200);
					}
				}, 500);
			} else {
				this.$notifyError(this, result.error, "An error occurred initiating the OAuth2 flow");
			}
		},
		displayRulesMet(displayOptions) {
			if (!displayOptions) return true;
			if (displayOptions.show) {
				for (let key in displayOptions.show) {
					const correspondingPropertyInData = this.form.data[key];
					const listOfPermissableValues = displayOptions.show[key];
					if (listOfPermissableValues.includes(correspondingPropertyInData)) {
						return true;
					}
				}
			}
			return false;
		},
		populateFormData() {
			this.$refs.form.resetFields();
			this.formRules.data = {};
			this.$set(this.form, "data", {});
			this.form.type = this.credentialInterface.name;
			this.credentialInterface.properties.forEach((property) => {
				let defaultVal = property.default;
				if (property.type === "fixedCollection") {
					defaultVal = {};
				}
				this.$set(this.form.data, property.name, defaultVal);
				if (property.type !== "hidden" && property.required) {
					this.formRules.data[property.name] = [
						{ required: true, message: "This field is required", trigger: "change" },
					];
				}
			});
			this.credentialInterfaceProperties = this.credentialInterface.properties;
		},
		populateFormDataFromExistingCredential() {
			this.form.name = this.credentialInterface.name;
			this.form.type = this.credentialInterface.type;

			if (this.isReadOnly) {
				const masked = {};
				Object.keys(this.credentialInterface.data || {}).forEach((k) => {
					masked[k] = "••••••••";
				});
				this.$set(this.form, "data", masked);
				this.credentialInterfaceProperties = this.credentialInterface.properties || [];
				return;
			}

			const normalized = JSON.parse(JSON.stringify(this.credentialInterface.data));
			this.$set(this.form, "data", normalized);

			if (!this.credentialInterface.properties) {
				console.warn("properties not yet available, data set but fields won't render yet");
				return;
			}

			this.credentialInterface.properties.forEach((property) => {
				if (property.type === "fixedCollection") {
					if (!normalized[property.name] || typeof normalized[property.name] !== "object") {
						normalized[property.name] = {};
					}
					property.options.forEach((option) => {
						const existing = normalized[property.name][option.name];
						if (property.typeOptions?.multipleValues) {
							if (!Array.isArray(existing)) {
								normalized[property.name][option.name] = [];
							} else {
								existing.forEach((item) => {
									Object.defineProperty(item, "ref", {
										enumerable: false,
										writable: true,
										configurable: true,
									});
									item.ref = option.name;
								});
							}
						} else {
							if (!existing || typeof existing !== "object" || Array.isArray(existing)) {
								normalized[property.name][option.name] = {};
							}
						}
					});
				}
			});

			this.$set(this.form, "data", normalized);
			this.$nextTick(() => {
				this.credentialInterfaceProperties = this.credentialInterface.properties;
			});
		},
	},
};
</script>

<style scoped>
.formWrapper {
	max-height: 550px;
}

.formWrapper /deep/ a {
	color: #210f3c !important;
}

.callbackUrl /deep/ input {
	cursor: pointer !important;
}
</style>
